import { EmptyState } from "cfw-lib-admin-ui/preact";
import { lazy, Suspense, useCallback, useRef, useState } from "react";
import { ArrowUpRight, Map, Plus, Search, Upload, X } from "lucide-react";
import type { AdminMap, MapDocument, MapSummary } from "../shared/types";
import { api, errorText } from "./api";
import { Notice, PageHeading, Status, updatedDate } from "./ui";
import "./source-import.css";
import { MapLibraryThumbnail } from "./MapLibraryThumbnail";

const PdfMapImport = lazy(() => import("./PdfMapImport"));

const blankMap = (id: string, title: string): MapDocument => ({
  schemaVersion: 1,
  id,
  title,
  description: "",
  geometry: {
    width: 1000,
    height: 700,
    floor: { x: 24, y: 24, width: 952, height: 652, radius: 8 },
    stands: [],
    entrances: [],
  },
  locations: [],
  labels: { stands: {}, entrances: {} },
  camera: { rotation: 0, pitch: 40 },
});

export function MapLibrary({
  maps,
  canEdit,
  openMap,
  onCreated,
}: {
  maps: MapSummary[];
  canEdit: boolean;
  openMap: (id: string) => void;
  onCreated: (map: AdminMap) => void;
}) {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState("");
  const [id, setId] = useState("");
  const [template, setTemplate] = useState("expocad");
  const [sourceUrl, setSourceUrl] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const file = useRef<HTMLInputElement>(null);
  const submitPdf = useRef<(() => Promise<void>) | null>(null);
  // Focus at mount; native autofocus can arrive after a visitor moves to another field.
  const focusMapName = useCallback((input: HTMLInputElement | null) => {
    input?.focus({ preventScroll: true });
  }, []);
  const filtered = maps.filter((map) => {
    const matchesQuery = `${map.title} ${map.id}`
      .toLowerCase()
      .includes(query.trim().toLowerCase());
    const published = map.publishedRevision !== null;
    return (
      matchesQuery &&
      (status === "all" || (status === "published" ? published : !published))
    );
  });
  async function create() {
    if (!canEdit || busy || template === "json") return;
    if (template === "pdf") {
      await submitPdf.current?.();
      return;
    }
    setBusy(true);
    setError("");
    try {
      if (template === "expocad") {
        onCreated(
          await api.importSource(id.trim(), title.trim(), sourceUrl.trim()),
        );
        return;
      }
      let document = blankMap(id.trim(), title.trim());
      if (template === "doha") {
        const { default: demo } = await import("../shared/demo-map.json");
        document = {
          ...(structuredClone(demo) as MapDocument),
          id: id.trim(),
          title: title.trim(),
        };
      }
      onCreated(await api.create(document));
    } catch (error) {
      setError(errorText(error));
    } finally {
      setBusy(false);
    }
  }
  async function importFile(selected: File | undefined) {
    if (!canEdit || busy || !selected) return;
    setError("");
    setBusy(true);
    try {
      if (selected.size > 1024 * 1024)
        throw new Error("Choose a map JSON file smaller than 1 MB.");
      const document = JSON.parse(await selected.text()) as MapDocument;
      onCreated(await api.create(document));
    } catch (error) {
      setError(
        error instanceof SyntaxError
          ? "This file is not valid JSON. Export a map document and try again."
          : errorText(error),
      );
    } finally {
      setBusy(false);
      if (file.current) file.current.value = "";
    }
  }
  return (
    <>
      <PageHeading
        title="Map library"
        description="Find a venue map or create one to start editing."
        className="em-page-heading"
      >
        {canEdit && (
          <div className="em-actions">
            <button
              className="em-button"
              disabled={busy}
              aria-expanded={creating}
              aria-controls="new-map-form"
              onClick={() => setCreating(!creating)}
            >
              <Plus size={17} />
              New map
            </button>
          </div>
        )}
      </PageHeading>
      {error && <Notice>{error}</Notice>}
      {creating && canEdit && (
        <form
          id="new-map-form"
          className="em-panel em-create-form"
          onSubmit={(event) => {
            event.preventDefault();
            void create();
          }}
        >
          <div className="em-panel-title">
            <div>
              <h2>Create a map</h2>
              <p>Choose a starting point. New maps begin as private drafts.</p>
            </div>
            <button
              type="button"
              className="em-icon-button"
              aria-label="Close new map form"
              onClick={() => setCreating(false)}
              disabled={busy}
            >
              <X size={19} />
            </button>
          </div>
          <label>
            Starting point
            <select
              value={template}
              disabled={busy}
              onChange={(event) => {
                setTemplate(event.currentTarget.value);
                setError("");
              }}
            >
              <option value="expocad">ExpoCAD source</option>
              <option value="pdf">PDF floor plan</option>
              <option value="doha">MWC Doha venue template</option>
              <option value="blank">Blank floor plan</option>
              <option value="json">Import JSON document</option>
            </select>
          </label>
          {template !== "json" && (
            <div className="em-form-grid">
              <label>
                Map name
                <input
                  ref={focusMapName}
                  required
                  disabled={busy}
                  maxLength={160}
                  value={title}
                  placeholder="MWC Doha 2026"
                  onChange={(event) => {
                    const value = event.currentTarget.value;
                    setTitle(value);
                    if (
                      !id ||
                      id ===
                        title
                          .toLowerCase()
                          .replace(/[^a-z0-9]+/g, "-")
                          .replace(/^-|-$/g, "")
                    )
                      setId(
                        value
                          .toLowerCase()
                          .replace(/[^a-z0-9]+/g, "-")
                          .replace(/^-|-$/g, ""),
                      );
                  }}
                />
              </label>
              <label>
                Map ID
                <input
                  required
                  disabled={busy}
                  maxLength={80}
                  pattern="[a-z0-9]+(?:-[a-z0-9]+)*"
                  value={id}
                  placeholder="mwc-doha-2026"
                  onChange={(event) => setId(event.currentTarget.value)}
                />
                <small>
                  Used in your API and embed URLs. Cannot be changed later.
                </small>
              </label>
            </div>
          )}
          {template === "json" && (
            <div>
              <p>
                Import an exported map document as a separate map using its
                existing name and ID. The ID must not already be in your
                library.
              </p>
              <input
                ref={file}
                type="file"
                accept=".json,application/json"
                disabled={busy}
                className="em-hidden"
                aria-label="Import a map JSON file"
                onChange={(event) =>
                  void importFile(event.currentTarget.files?.[0])
                }
              />
              <button
                type="button"
                className="em-button"
                disabled={busy}
                onClick={() => file.current?.click()}
              >
                <Upload size={16} />
                {busy ? "Importing…" : "Choose JSON file"}
              </button>
            </div>
          )}
          {template === "expocad" && (
            <label>
              <span id="source-import-label">Source floor plan URL</span>
              <input
                type="url"
                required
                disabled={busy}
                maxLength={2048}
                value={sourceUrl}
                placeholder="https://gsma.expocad.com/Events/event-id/index.html"
                aria-labelledby="source-import-label"
                aria-describedby="source-import-help"
                onChange={(event) => setSourceUrl(event.currentTarget.value)}
              />
              <small id="source-import-help">
                Imports booths and locations into a draft for review. The source
                URL stays private to this admin workspace and is never included
                in public maps or embeds.
              </small>
            </label>
          )}
          {template === "pdf" && (
            <Suspense fallback={<p role="status">Loading PDF import…</p>}>
              <PdfMapImport
                id={id.trim()}
                title={title.trim()}
                submitRef={submitPdf}
                onBusy={setBusy}
                onCreated={onCreated}
              />
            </Suspense>
          )}
          {busy && template === "expocad" && (
            <p className="em-source-progress" role="status">
              Fetching the source floor plan and preparing your draft…
            </p>
          )}
          {template !== "json" && template !== "pdf" && (
            <div className="em-actions">
              <button className="em-button" disabled={busy}>
                {template === "expocad"
                  ? busy
                    ? "Importing…"
                    : "Import draft"
                  : busy
                    ? "Creating…"
                    : "Create draft"}
              </button>
            </div>
          )}
        </form>
      )}
      <div className="em-list-toolbar em-library-filters">
        <label className="em-search">
          <Search size={17} />
          <span className="em-sr-only">Search maps</span>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.currentTarget.value)}
            placeholder="Find a map…"
          />
        </label>
        <label>
          <span className="em-sr-only">Map status</span>
          <select
            value={status}
            onChange={(event) => setStatus(event.currentTarget.value)}
          >
            <option value="all">All maps</option>
            <option value="published">Published</option>
            <option value="drafts">Drafts</option>
          </select>
        </label>
        <span className="em-library-result-count" role="status">
          {filtered.length} {filtered.length === 1 ? "map" : "maps"}
        </span>
      </div>
      {!filtered.length ? (
        <EmptyState
          className="em-panel"
          icon={<Map size={38} />}
          title={
            maps.length
              ? "No maps match your filters"
              : "Your first map starts here"
          }
          text={
            maps.length
              ? "Try a different map name or status."
              : canEdit
                ? "Choose New map to import a floor plan, use a venue template or start from scratch."
                : "Your team’s maps will appear here when they’re created."
          }
        />
      ) : (
        <div className="em-map-grid">
          {filtered.map((map) => (
            <button
              key={map.id}
              className="em-map-card"
              onClick={() => openMap(map.id)}
            >
              <div className="em-map-card-art" aria-hidden="true">
                <MapLibraryThumbnail
                  key={`${map.id}:${map.revision}`}
                  map={map}
                />
                <span>
                  <ArrowUpRight size={18} />
                </span>
              </div>
              <div className="em-map-card-body">
                <Status map={map} />
                <h3>{map.title}</h3>
                <p>{map.description || "Interactive venue map"}</p>
                <div className="em-map-card-meta">
                  <span>
                    {map.standCount} booths · {map.locationCount} locations
                  </span>
                  <span>Updated {updatedDate(map.updatedAt)}</span>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </>
  );
}
